import axios from "axios";
import {
  listarEmpresasPaginadas,
  listarEmpresasSimples,
  buscarEmpresaPorNomeCnpj,
  buscarEmpresaPorId,
} from "./empresaService.js";
import {
  listarAcompanhamentosMe,
  buscarStatusCompletoDaEmpresa as buscarStatusCompletoApi,
} from "./acompanhamentoService.js";
import {
  normalizarListaEmpresas,
  normalizarEmpresaResposta,
  obterIdTca,
} from "../utils/empresa.js";
import {
  obterIdentidadeSessao,
  obterNivelAcessoPagina,
  obterDocumentoUsuarioLogadoPagina,
} from "../utils/session.js";
import {
  NIVEL_EMPRESA,
  NIVEL_ESTUDANTE,
  NIVEL_CONSULTOR,
  NIVEL_SUPEX,
  empresaFoiCadastradaPeloConsultor,
} from "../utils/permissoes.js";
import { API_BASE_URL } from "../../../../js/api.js";
import { jwtDecode } from "jwt-decode";
import {
  normalizarEmpresaAcompanhamentoItem,
  deduplicarAcompanhamentosPorEmpresa,
} from "../mappers/empresaMapper.js";
import {
  cadastroRequerSupex,
  normalizarListaSolicitacoesExcecao,
} from "../mappers/supexMapper.js";
import { montarDescricaoEmpresa } from "../utils/empresa.js";

/**
 * Resolve a empresa do contexto atual para o nível empresa.
 * Empresa sempre fica presa ao próprio documento, ignorando
 * query/storage de terceiros (espelha o CadastroEmpresa).
 */
async function resolverEmpresaDoNivelEmpresa(token) {
  const documentoUsuario = obterDocumentoUsuarioLogadoPagina();
  if (!documentoUsuario) return null;

  const empresaDoUsuario = await buscarEmpresaPorNomeCnpj(documentoUsuario, token);
  if (!empresaDoUsuario?.id) return null;

  if (typeof window !== "undefined") {
    window.localStorage.setItem(
      "empresa_id_acompanhamento",
      String(empresaDoUsuario.id),
    );
    window.localStorage.setItem(
      "empresa_busca_acompanhamento",
      montarDescricaoEmpresa(empresaDoUsuario),
    );
  }

  return empresaDoUsuario;
}

async function listarEmpresasDoConsultor({
  token,
  usuarioIdLogado,
  documentoLogado,
}) {
  const filtrar = (lista) =>
    lista
      .map(normalizarEmpresaAcompanhamentoItem)
      .filter(Boolean)
      .filter((item) =>
        empresaFoiCadastradaPeloConsultor(item, usuarioIdLogado, documentoLogado),
      );

  try {
    const listaPaginada = await listarEmpresasPaginadas({ token });
    return deduplicarAcompanhamentosPorEmpresa(
      filtrar(normalizarListaEmpresas(listaPaginada)),
    );
  } catch (error) {
    console.warn(
      "Falha na listagem paginada do consultor, tentando fallback simples:",
      error,
    );
  }

  try {
    const listaSimples = await listarEmpresasSimples(token);
    return deduplicarAcompanhamentosPorEmpresa(
      filtrar(normalizarListaEmpresas(listaSimples)),
    );
  } catch (error) {
    console.warn("Fallback simples do consultor também falhou:", error);
  }

  return [];
}

function aplicarFiltroPorNivel(lista, nivel, identidade) {
  if (nivel === NIVEL_CONSULTOR) {
    return lista.filter((item) =>
      empresaFoiCadastradaPeloConsultor(
        item,
        identidade.usuarioId,
        identidade.documento,
      ),
    );
  }
  if (nivel === NIVEL_SUPEX) {
    return lista.filter((item) => {
      const raw = item?.raw || item || {};
      return cadastroRequerSupex(raw, raw, [], item);
    });
  }
  return lista;
}

/**
 * Carrega acompanhamentos a partir da rota dedicada /acompanhamentos/me
 * com fallback para listagem geral de empresas.
 *
 * Atenção: nenhum servico recebe `page`/`limit` porque o backend atual
 * retorna 500 com esses parametros.
 */
async function listarAcompanhamentosGenericos({
  token,
  nivel,
  identidade,
  busca,
  empresaId,
}) {
  try {
    const payload = await listarAcompanhamentosMe({
      token,
      empresaId,
      busca,
    });
    const lista = normalizarListaEmpresas(payload)
      .map(normalizarEmpresaAcompanhamentoItem)
      .filter(Boolean);
    return deduplicarAcompanhamentosPorEmpresa(
      aplicarFiltroPorNivel(lista, nivel, identidade),
    );
  } catch {
    /* tenta fallback abaixo */
  }

  try {
    const lista = await listarEmpresasPaginadas({
      token,
      busca,
      empresaId,
    });
    const itens = normalizarListaEmpresas(lista)
      .map(normalizarEmpresaAcompanhamentoItem)
      .filter(Boolean);
    return deduplicarAcompanhamentosPorEmpresa(
      aplicarFiltroPorNivel(itens, nivel, identidade),
    );
  } catch {
    /* tenta fallback secundário */
  }

  try {
    const lista = await listarEmpresasSimples(token);
    const itens = normalizarListaEmpresas(lista)
      .map(normalizarEmpresaAcompanhamentoItem)
      .filter(Boolean);
    return deduplicarAcompanhamentosPorEmpresa(
      aplicarFiltroPorNivel(itens, nivel, identidade),
    );
  } catch (error) {
    console.warn("Fallback simples de empresas também falhou:", error);
  }

  return [];
}

/**
 * Lista as autorizações vinculadas ao CPF do estudante logado.
 *
 * O backend expõe um endpoint dedicado por nível de programa:
 *   - TCE (programa 0):           GET /tce/estudante/<cpf>/autorizacoes
 *   - TCA Empregador (programa 2): GET /tca-empregador/estudante/<cpf>/autorizacoes
 *
 * Ambos retornam um array (ou `{ data: [...] }`) com itens no formato
 * `{ id, nome, documento, status, tipo_programa, raw }`. Cada item é
 * tratado de forma individual para que um estudante vinculado a várias
 * autorizações veja uma entrada por processo, sem mistura de dados.
 */
async function listarAutorizacoesDoEstudante(token) {
  if (typeof window === "undefined") return [];
  const t = token || window.localStorage.getItem("token");
  if (!t) return [];

  let decoded = null;
  try {
    decoded = jwtDecode(t);
  } catch {
    return [];
  }

  const cpfEstudante = String(
    decoded?.cpf || decoded?.documento || decoded?.sub || "",
  )
    .replace(/\D/g, "")
    .slice(0, 11);

  if (!cpfEstudante) return [];

  const tipoPrograma = Number(decoded?.tipo_programa);
  const endpoints = [];
  if (tipoPrograma === 0) {
    endpoints.push(`${API_BASE_URL}/tce/estudante/${cpfEstudante}/autorizacoes`);
  } else if (tipoPrograma === 2) {
    endpoints.push(
      `${API_BASE_URL}/tca-empregador/estudante/${cpfEstudante}/autorizacoes`,
    );
  } else {
    endpoints.push(
      `${API_BASE_URL}/tce/estudante/${cpfEstudante}/autorizacoes`,
      `${API_BASE_URL}/tca-empregador/estudante/${cpfEstudante}/autorizacoes`,
    );
  }

  let agregada = [];
  for (const url of endpoints) {
    try {
      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${t}` },
      });
      const payload = resp?.data;
      const lista = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      agregada = agregada.concat(lista);
    } catch (err) {
      console.warn(
        "[Acompanhamento] Falha ao listar autorizações do estudante:",
        err?.message,
      );
    }
  }

  return normalizarListaEmpresas(agregada)
    .map(normalizarEmpresaAcompanhamentoItem)
    .filter(Boolean);
}

/**
 * Carrega acompanhamentos do usuário conforme o nível de acesso.
 */
export async function carregarAcompanhamentosDoUsuario(
  token,
  page = 1,
  limit = 50,
  contexto = {},
) {
  const nivel = obterNivelAcessoPagina();
  const identidade = obterIdentidadeSessao();

  if (nivel === NIVEL_EMPRESA) {
    const unica = await resolverEmpresaDoNivelEmpresa(token);
    return unica?.id ? [{ ...unica, raw: unica }] : [];
  }

  if (nivel === NIVEL_CONSULTOR) {
    return await listarEmpresasDoConsultor({
      token,
      usuarioIdLogado: identidade.usuarioId,
      documentoLogado: identidade.documento,
    });
  }

  if (nivel === NIVEL_ESTUDANTE) {
    return await listarAutorizacoesDoEstudante(token);
  }

  return await listarAcompanhamentosGenericos({
    token,
    nivel,
    identidade,
    busca: contexto.busca,
    empresaId: contexto.empresaId,
  });
}

/**
 * Atualiza o status de uma empresa específica e devolve um modelo
 * pronto para a UI (passando pelos mappers de SUPEX, rejeição e status).
 *
<<<<<<< HEAD
 * Suporta os dois programas que podem aparecer no fluxo do estudante:
 *   - TCE (programa 0): GET /tce/<id>
 *   - TCA Empregador (programa 2): GET /tca-empregador/<id>
 *
 * A escolha da rota é feita pelo `tipo_programa` do item (raw) ou, em
 * último caso, pelo JWT do usuário logado.
 */
export async function buscarStatusCompletoDaEmpresa(empresaRef, token, signal) {
  if (!empresaRef) throw new Error("Empresa inválida");

  const raw = empresaRef.raw || empresaRef || {};
  const tipoProgramaItem = Number(
    raw?.tipo_programa ?? raw?.tipoPrograma ?? 0,
  );

  let tokenDecoded = null;
  try {
    tokenDecoded = jwtDecode(token || localStorage.getItem("token") || "");
  } catch {
    tokenDecoded = null;
  }
  const tipoProgramaJwt = Number(tokenDecoded?.tipo_programa ?? 0);

  const programaEfetivo =
    Number.isFinite(tipoProgramaItem) && tipoProgramaItem > 0
      ? tipoProgramaItem
      : tipoProgramaJwt;

  const endpoint =
    programaEfetivo === 0
      ? `${API_BASE_URL}/tce/${empresaRef.id}`
      : `${API_BASE_URL}/tca-empregador/${empresaRef.id}`;

  try {
    const resp = await axios.get(endpoint, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload = resp?.data;
    const empresa = normalizarEmpresaResposta(payload) || payload;
    const listaExcecoes = normalizarListaSolicitacoesExcecao(payload, empresa);

    return {
      payload,
      empresa,
      listaExcecoes,
      fonte: "api",
    };
  } catch (error) {
    // Endpoint /:id não está exposto no backend (404).
    // Não falhamos a UI por isso — caímos no `raw` que já veio da listagem.
    const eh404 =
      error?.response?.status === 404 ||
      /status code 404/i.test(error?.message || "");
    if (!eh404) throw error;

    const empresa = normalizarEmpresaResposta(raw) || raw;
    const listaExcecoes = normalizarListaSolicitacoesExcecao(raw, empresa);

    return {
      payload: raw,
      empresa,
      listaExcecoes,
      fonte: "cache",
    };
  }
}

/**
 * Lê a lista de exceções de uma empresa no contexto da listagem atual.
 * Útil quando a UI já tem o `raw` em memória.
 */
export function obterExcecoesDoItem(item) {
  const raw = item?.raw || {};
  return normalizarListaSolicitacoesExcecao(raw, raw);
}

export function obterRequerSupexDoItem(item) {
  const raw = item?.raw || item || {};
  const excecoes = obterExcecoesDoItem(item);
  return cadastroRequerSupex(raw, raw, excecoes, item);
}
